// src/encounter/pre-encounter-context-strip.tsx
// The pre-encounter TOP CONTEXT chrome (PROD-1588 round 3, overlay-gate iteration 2 — the
// E2 pre-encounter frame 1736:156432 renders it; the React surface shipped without it).
//
// WAVE 4 (L5): both pieces are now THIN adapters over the wave-3 DS molecules — the
// hand-rolled 43px tab strip and the interim EncounterContextStrip composition retired
// in the same motion:
//   1. PreEncounterTabStrip → DS `EncounterTopBar` (B2 — the missing top bar): back chevron
//      + Encounter | Chart tabs supplied as DATA (D9-neutral: whatever navigation model D9
//      locks, the molecule renders it).
//   2. PreEncounterContextBlock → DS `VisitInfoStrip` (B5 — the room · time · reason
//      caption/value strip) + the status pill / interactive status control slot.
//
// ANTI-FICTION: every cell renders only when its datum resolves — no fabricated room
// ("Room 1"), no invented status. `room` currently has NO feed (list endpoints ship only
// roomId — the same gap the in-encounter VisitInfoStrip documents), so the page omits it
// and the strip renders time-only until the appointment-detail source is plumbed.
// PROD-1986 — the Chart tab is an IN-PLACE tab, not a route out of the encounter. Flutter PA
// runs the whole encounter on ONE screen with ONE TabController (encounter_screen.dart:981-1004
// tabsHeader + :1049 tabViewContent `case "Chart"` → ChartMainView; pre-encounter :1459-1490
// tabViewInitial tab 1 → chartViewMainPanel), so selecting Chart swaps the tab BODY and leaves
// the patient-summary banner + the Note/Orders side panel mounted. This strip is therefore a
// CONTROLLED tab strip (`activeTab` + `onTabChange`) — it no longer carries an `onOpenChart`
// navigation escape hatch, which unmounted the encounter (draft QR + autosave torn down) and
// landed the user on the patient-EDIT shell.
// The Chart tab renders only when the host can actually mount the chart (patientId resolved →
// `onTabChange` supplied); no dead tab.
import { useState, type ReactNode } from 'react';
import {
  Avatar,
  Button,
  EncounterTopBar,
  VisitInfoStrip,
  StatusChip,
  SubTabs,
  type EncounterTopBarTab,
  type PatientHeaderData,
  type PatientHeaderDemographics,
  type SubTabDef,
  type VisitInfoCell,
} from '@scripsteam/scrips-signal-ds';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { PageHeader } from '@/shared/ui/components/page-header';
import { ageInWords, patientIdentityParts } from './lib/patient-identity';
import { formatVisitTime } from './lib/visit-time';
import { useDesignV2 } from './lib/design-v2';
import { pmStatusLabel, pmStatusSemantic } from '@/lib/pm-appointment-status';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** The encounter's LATCHING top-level tabs (Flutter `state.tabs`, ui_data.dart:127) — the two that
 *  own a body. "Malaffi" is deliberately NOT a member: Flutter's tab view for it is an empty
 *  Container and activating it opens the SSO popup then `animateTo(0)`s straight back, so it is an
 *  action (`onOpenMalaffi`) that never becomes the selected tab. */
export type EncounterTopTab = 'encounter' | 'terrain' | 'chart' | 'timeline' | 'documents';

/** The tabs the one page header can name (Encounter · Chart · Timeline · Documents; the DS tab strip names no others). */
export type EncounterHeaderTab = EncounterTopTab;

/** Search param that carries the active top tab on the encounter route (PROD-1986):
 *  `/encounter/:appointmentId?tab=chart`. Deliberately NOT the chart's own inner-tab param
 *  (`inner`, patient-edit-tabs.ts) — outer selection and inner chart tab are independent, so a
 *  deep link can name both. */
export const ENCOUNTER_TOP_TAB_PARAM = 'tab';

export interface PreEncounterVisitContext {
  /** Room display name — NO feed yet (list endpoints ship only roomId); omitted → time-only cell. */
  room?: string;
  /** Appointment start ISO — the "Today, 9:00 AM" value. */
  startISO?: string;
  /** Reason for visit (reasonCode.display). */
  reason?: string;
  /** Appointment status (canonical code or display) — the status pill; absent → no pill. */
  appointmentStatus?: string;
  /** Back chevron (Flutter BigChevronBack). Absent → chevron hidden. */
  onBack?: () => void;
  /** Active top tab (PROD-1986). The host owns it (an encounter-route search param), so Chart
   *  is a real selectable tab state — not a hardcoded "Encounter is always active". */
  activeTab?: EncounterTopTab;
  /** Tab activation. Absent → the Chart tab does not render, because the host cannot mount the
   *  chart yet (unresolved patientId). Anti-fiction: one prop gates the tab and drives it. */
  onTabChange?: (tab: EncounterHeaderTab) => void;
  /** The right-end 16px search glyph (E1 interaction I16). HONEST wiring only: supply it
   *  when the host routes to a REAL search surface (the shipped /patients patient search);
   *  absent → the glyph does not render (the DS contract — no dead affordance). D9. */
  onSearch?: () => void;
  /** Open the Malaffi HIE SSO popup. Supplied exactly when the org's `integrationType` is Malaffi
   *  — the single condition Flutter gates its third tab on (ui_data.dart:130-133). Absent → the
   *  Malaffi tab does not render. Deliberately NOT also gated on the MRN/user id resolving: those
   *  are read at click time (see useMalaffiSso), matching Flutter. */
  onOpenMalaffi?: () => void;
}

/** The full-width Encounter | Chart | Malaffi tab strip — the DS EncounterTopBar molecule (B2/L5),
 *  rendered with its own chrome (which matches the published Flutter strip — see the JSX comment).
 *  D9 NAVFIRST (Samer decision strip 2026-07-22): this strip mounts on ALL encounter
 *  surfaces — the pre-encounter rail AND the in-encounter step shell — not just
 *  pre-encounter. Tabs are DATA (D9-neutral): "Encounter" is this surface (active);
 *  "Chart" appears only when the host can route it. Back/search render only when the
 *  host supplies them.
 *
 *  MALAFFI (Flutter ui_data.dart:127-137 + encounter_screen.dart:182-188): the third tab is an
 *  ACTION, not a destination — Flutter's tab view for it is an empty Container, and activating it
 *  opens the SSO popup then immediately `animateTo(0)`s back to Encounter. Ported literally: the
 *  strip fires `onOpenMalaffi` and `activeId` stays "encounter", so the tab never latches. */
const SEX_LABEL: Record<NonNullable<PatientHeaderDemographics['sex']>, string> = { M: 'Male', F: 'Female', X: 'Other' };

/** The patient page header on the encounter (Antonin, 15 Oct, consistency pass: "also the patient header"; prototype
 *  `PatientView` header). The same one PageHeader the patient record draws (`patient-edit-page.tsx`): the 48px photo,
 *  the name as the title over one line of facts ("16y · Female · DOB 1 Jan 2010 · MRN 000-031-3740", the D-68
 *  identity parts plus sex), and the encounter's own tabs on the right as the header's underline tabs. Back stays (in
 *  a started encounter it returns to the pre-encounter, Andrew 24 Aug), as a quiet icon before the photo (the lucide
 *  chevron the default path draws elsewhere: the DS "back" font glyph renders as a blank box here). Same tabs
 *  and handlers as the DS strip below; Malaffi stays an action that never latches. */
function EncounterPatientHeader({
  patient,
  demographics,
  tabs,
  activeId,
  onSelect,
  onBack,
  leftToggle,
  startedTag,
  onOpenMalaffi,
  oneLook,
  headerAction,
  importance,
  status,
  cardMoved,
}: {
  /** A quiet action after Malaffi beside the name (staff chat's "Message a colleague", SR-14). */
  headerAction?: ReactNode;
  /** The rail's patient card is not drawn (`?x=rail-fixes` with the identity in this header): its language and importance show here. */
  cardMoved?: boolean;
  /** The visit's status ("Checked in"). After `startedTag` by the name; with `?x=rail-fixes` it opens the subline instead. */
  status?: ReactNode;
  /** Frame v2 (design EncounterHeader, 21-10): the facts read "52y · Female · DOB … · <language> · MRN …". */
  oneLook?: boolean;
  /** The patient's importance (VIP and the like). Drawn only with `?x=rail-fixes` and `cardMoved`. */
  importance?: { code?: string | null; display?: string | null } | null;
  patient: PatientHeaderData;
  demographics?: PatientHeaderDemographics;
  tabs: EncounterTopBarTab[];
  activeId: string;
  onSelect: (id: string) => void;
  onBack?: () => void;
  leftToggle?: ReactNode;
  startedTag?: ReactNode;
  /** Opens the Malaffi HIE SSO (a popup window: external). Drawn as its own secondary button, not a tab. */
  onOpenMalaffi?: () => void;
}) {
  // `?x=rail-fixes` (PROD-3383/3384, Antonin's 3 Nov Loom 1): the rail's patient card is gone, so what only it showed moves here: the
  // language (with "Interpreter Required", as the card wrote it) joins the facts, the importance rides the photo (VIP) or sits by the
  // name as the card's chip; and the status ("Checked in") opens the subline instead of sitting beside the name. Read once per mount.
  const [railFixes] = useState(() => isEncounterFlagOn('railFixes'));
  const parts = patientIdentityParts(patient, demographics);
  const sex = demographics?.sex ? SEX_LABEL[demographics.sex] : undefined;
  const cardLanguage = [demographics?.language?.trim(), demographics?.interpreterRequired ? 'Interpreter Required' : undefined].filter(Boolean).join(' - ');
  // Only where the card is gone (`cardMoved`): with it still drawn (e.g. `?x=-previsit-header`) the card shows them, never twice.
  const moved = railFixes && !!cardMoved;
  const language = moved ? cardLanguage || undefined : oneLook ? demographics?.language?.trim() || undefined : undefined;
  const facts = [oneLook ? parts.age : ageInWords(parts.age), sex, parts.dob, language, parts.mrn].filter((f): f is string => !!f);
  const importanceCode = moved ? importance?.code?.trim() || undefined : undefined;
  // The words a person reads: the display, else the code (a coded value is never shown raw when it has a display).
  const importanceText = moved ? importance?.display?.trim() || importanceCode : undefined;
  // VIP is decided on the code, as the card's avatar did.
  const vip = importanceCode?.toUpperCase() === 'VIP';
  const importanceChip =
    importanceText && !vip ? (
      <StatusChip size="xs" semantic="neutral" tone="soft" title={importanceText} data-slot="header-importance">
        {importanceText}
      </StatusChip>
    ) : null;
  const tagStatus = railFixes ? null : status;
  const factsLine = facts.length > 0 ? facts.join(' · ') : undefined;
  const meta =
    railFixes && status != null ? (
      <span data-slot="header-status-first" className="inline-flex max-w-full items-center gap-2 align-middle">
        <span className="inline-flex shrink-0">{status}</span>
        {factsLine ? <span className="min-w-0 truncate">{factsLine}</span> : null}
      </span>
    ) : (
      factsLine
    );
  return (
    <div data-slot="encounter-patient-header" data-left-toggle-in={leftToggle ? 'header' : undefined}>
      <PageHeader
        leading={
          <>
            {onBack ? (
              <Button type="button" variant="tertiary" size="sm" aria-label="Back" icon={<ChevronLeft aria-hidden />} onClick={onBack} />
            ) : null}
            <Avatar scale="compact" tone="patient" name={parts.name} src={patient.avatar || undefined} size={48} importance={vip ? importanceCode : undefined} />
          </>
        }
        title={parts.name}
        tag={
          startedTag != null || tagStatus != null || importanceChip || onOpenMalaffi || headerAction != null ? (
            <div className="page-header-tag-group">
              {startedTag}
              {tagStatus}
              {importanceChip}
              {onOpenMalaffi ? (
                <Button type="button" variant="secondary" size="sm" data-slot="open-malaffi" icon={<ExternalLink aria-hidden />} iconPosition="trailing" onClick={onOpenMalaffi}>
                  Malaffi
                </Button>
              ) : null}
              {headerAction}
            </div>
          ) : undefined
        }
        meta={meta}
        tabs={
          <SubTabs
            tabs={tabs.filter((t) => t.id !== 'malaffi').map((t): SubTabDef => ({ id: t.id, label: t.label }))}
            value={activeId}
            onValueChange={onSelect}
          />
        }
        /* The page's actions are not here (Antonin, 15 Oct: "No button near tabs in header"): Start / Continue and Reschedule live
           in the step bar / the long page's header. The one exception (16 Oct, "open malaffi should be a different button, not a tab"):
           the Malaffi SSO, now in the left part of the header beside the name (16 Oct), a secondary button with the outwards icon (it opens the HIE portal in a popup window). No Malaffi
           logo asset exists in the repo or the DS, so none is drawn (a gap). */
      />
      {/* The left panel's one toggle, on the header's hairline (prototype RailToggle; see left-panel-fold.tsx). */}
      {leftToggle}
    </div>
  );
}

export function PreEncounterTabStrip({
  activeTab = 'encounter',
  onTabChange,
  onBack,
  onSearch,
  onOpenMalaffi,
  patient,
  demographics,
  leftToggle,
  startedTag,
  documentsAvailable,
  timelineAvailable,
  timelineLabel = 'Timeline',
  terrainAvailable,
  oneLook,
  headerAction,
  importance,
  status,
  cardMoved,
}: Omit<Pick<PreEncounterVisitContext, 'activeTab' | 'onTabChange' | 'onBack' | 'onSearch' | 'onOpenMalaffi'>, 'activeTab'> & {
  activeTab?: EncounterHeaderTab;
  /** The visit's own Documents tab (the encounter-scoped list the Docs step draws). Offered only when the host can mount it
   *  (the encounter exists): no dead tab. Only with `patient`. */
  documentsAvailable?: boolean;
  /** The visit's care-pathway Timeline as its own tab (it reads the appointment's history). Offered only when the host has an
   *  appointment to read for: no dead tab. Only with `patient`. */
  timelineAvailable?: boolean;
  /** The Timeline slot's name: "Care plan" when the host shows the care plan there (`?x=care-plan-tab`). */
  timelineLabel?: string;
  /** Terrain (`?x=terrain`): the patient's body systems, its own tab right after Encounter. Offered only when the host mounts it
   *  (switch on, patient resolved): no dead tab. Only with `patient`. */
  terrainAvailable?: boolean;
  /** The left panel's fold toggle (left-panel-fold.tsx), drawn on the header's bottom hairline. Only with `patient`. */
  leftToggle?: ReactNode;
  /** The started visit's status tag after the name ("Checked In"). Only with `patient`. */
  startedTag?: ReactNode;
  /** Frame v2's one look (design EncounterHeader, 21-10): the Chart tab reads "Patient chart" and the facts carry the language
   *  before the MRN. Only with `patient`; absent, the header is as before (the legacy frame, the pre-encounter). */
  oneLook?: boolean;
  /** A quiet action in the patient page header after Malaffi (staff chat's "Message a colleague", SR-14). Only with `patient`. */
  headerAction?: ReactNode;
  /** The patient: when given, the strip is the patient page header (photo, name, facts, tabs right) instead of the
   *  DS EncounterTopBar. Absent → the DS strip exactly as before (stories, tests, no-patient paths). */
  patient?: PatientHeaderData;
  demographics?: PatientHeaderDemographics;
  /** The visit's status ("Checked in"): after `startedTag`, or opening the subline with `?x=rail-fixes`. Only with `patient`. */
  status?: ReactNode;
  /** The patient's importance, for the header with `?x=rail-fixes` and `cardMoved` (see EncounterPatientHeader). Only with `patient`. */
  importance?: { code?: string | null; display?: string | null } | null;
  /** The rail draws no patient card (see EncounterPatientHeader). Only with `patient`. */
  cardMoved?: boolean;
}) {
  // Design sheet v1.1 §2.6 (switch ON only): Malaffi is an SSO link to the HIE portal, not a data
  // tab, so its words say so ("Opens Malaffi"). Behaviour unchanged. The sheet's `external` glyph
  // needs an icon slot `EncounterTopBarTab` does not have (DS 2.14.0: id · label · disabled) — GAP.
  const designV2 = useDesignV2();
  const tabs: EncounterTopBarTab[] = [
    { id: 'encounter', label: 'Encounter' },
    // Terrain, beside Encounter (Andrew, 4 Nov: the clinician switches between the two in the header). Patient path only.
    ...(patient && onTabChange && terrainAvailable ? [{ id: 'terrain', label: 'Terrain' }] : []),
    // The page header (patient path) says "Chart", as the prototype's header does; the bare DS strip keeps its old words.
    ...(onTabChange ? [{ id: 'chart', label: oneLook ? 'Patient chart' : patient || !designV2 ? 'Chart' : 'Patient chart' }] : []),
    // The care-pathway Timeline, its own tab between Chart and Documents (patient path only).
    // `?x=care-plan-tab` (dev default): the host names this slot "Care plan" (Samer's demo); else "Timeline".
    ...(patient && onTabChange && timelineAvailable ? [{ id: 'timeline', label: timelineLabel }] : []),
    // The visit's documents (patient path only): the Docs step's own encounter-scoped list, as a tab.
    ...(patient && onTabChange && documentsAvailable ? [{ id: 'documents', label: 'Documents' }] : []),
    // Index 2 in Flutter's list, i.e. after Chart. Presence-gated on a real handler —
    // PROD-1830 shipped the HIE SSO route, so this is never a dead tab.
    ...(onOpenMalaffi ? [{ id: 'malaffi', label: designV2 ? 'Opens Malaffi' : 'Malaffi' }] : []),
  ];
  const activeId =
    activeTab === 'terrain' && patient && onTabChange && terrainAvailable
      ? 'terrain'
      : activeTab === 'chart' && onTabChange
      ? 'chart'
      : activeTab === 'timeline' && patient && onTabChange && timelineAvailable
        ? 'timeline'
        : activeTab === 'documents' && patient && onTabChange && documentsAvailable
          ? 'documents'
          : 'encounter';
  const select = (id: string) => {
    if (id === 'malaffi') {
      onOpenMalaffi?.();
      return;
    }
    onTabChange?.(id === 'terrain' ? 'terrain' : id === 'chart' ? 'chart' : id === 'timeline' ? 'timeline' : id === 'documents' ? 'documents' : 'encounter');
  };
  if (patient) {
    // The search glyph has no place here: the top bar's search is on every page.
    return (
      <EncounterPatientHeader
        patient={patient}
        demographics={demographics}
        tabs={tabs}
        activeId={activeId}
        onSelect={select}
        onBack={onBack}
        leftToggle={leftToggle}
        startedTag={startedTag}
        onOpenMalaffi={onOpenMalaffi}
        oneLook={oneLook}
        headerAction={headerAction}
        importance={importance}
        status={status}
        cardMoved={cardMoved}
      />
    );
  }
  return (
    <EncounterTopBar
      /* Rendered UNSTYLED on purpose (PROD-1549, 2026-09-05). The DS molecule's own chrome —
         44px strip, 24px back chevron at the DS's 16px inset (round 4b, which retired this app's
         5px `.pa-pre-encounter-top-bar` nudge), 15px tabs, active in the brand colour with no
         underline — IS the published Flutter strip, measured at 1920 / zoom 100 % / DPR 1: strip
         44, chevron ink x 80-87, "Encounter" x 120-188, 25px between tabs. */
      tabs={tabs}
      // Chart can only be active when it is actually offered — a stale ?tab=chart deep link on a
      // surface that cannot mount the chart falls back to Encounter rather than selecting nothing.
      // Malaffi never appears here: it is an ACTION, so it never latches (see below).
      activeId={activeTab === 'chart' && onTabChange ? 'chart' : 'encounter'}
      onTabChange={(id) => {
        // Malaffi opens the SSO popup and leaves the selection where it was — Flutter's own
        // `animateTo(0)` behaviour (encounter_screen.dart:182-188), not a tab change.
        if (id === 'malaffi') {
          onOpenMalaffi?.();
          return;
        }
        onTabChange?.(id === 'chart' ? 'chart' : 'encounter');
      }}
      onBack={onBack}
      onSearch={onSearch}
      ariaLabel="Encounter, chart and Malaffi"
    />
  );
}

/**
 * PROD-2743 · frame v2 (DARK). The started encounter's WORKSPACE tabs, over the centre and the
 * right column only (the chart stays put on the leading edge across tabs).
 *
 * Two, both of which exist today — no placeholder tabs (the long page's header, PreEncounterTabStrip, carries Encounter · Chart ·
 * Timeline · Documents):
 *   Encounter      the step canvas (under the S·O·A·P ribbon)
 *   Patient chart  the existing chart body (EncounterChartPanel), the tab the strip above calls "Chart"
 * "Patient chart" renders only when the host can mount the chart (patient id resolved), the same
 * anti-fiction gate the Encounter | Chart strip uses. Malaffi is not a workspace tab (ruled list);
 * the back chevron keeps today's in-encounter meaning: back to the pre-encounter, not off the route.
 */
export type EncounterWorkspaceTab = 'encounter' | 'chart';

export function EncounterWorkspaceTabStrip({
  activeTab,
  onTabChange,
  chartAvailable,
  onBack,
  onOpenMalaffi,
  endSlot,
}: {
  activeTab: EncounterWorkspaceTab;
  onTabChange: (tab: EncounterWorkspaceTab) => void;
  chartAvailable: boolean;
  onBack?: () => void;
  /** The strip's trailing end (DS EncounterTopBar `endSlot`): the D-68 compact patient identity, which
   *  draws itself only while the chart card is not visible (frame-v2-patient-identity.tsx). Absent →
   *  the strip is exactly as before. */
  endSlot?: ReactNode;
  /** Malaffi HIE SSO. The started visit kept it in the old frame (Flutter carries the same third tab
   *  into the started encounter); frame v2 dropped it, so a Malaffi practice lost the HIE once the
   *  visit started. Same ACTION semantics as PreEncounterTabStrip: it opens the SSO and never
   *  becomes the selected tab. Absent (non-Malaffi org / no MRN) → no tab. */
  onOpenMalaffi?: () => void;
}) {
  const tabs: EncounterTopBarTab[] = [
    { id: 'encounter', label: 'Encounter' },
    ...(chartAvailable ? [{ id: 'chart', label: 'Patient chart' }] : []),
    ...(onOpenMalaffi ? [{ id: 'malaffi', label: 'Opens Malaffi' }] : []),
  ];
  const active = activeTab === 'chart' && !chartAvailable ? 'encounter' : activeTab;
  return (
    <EncounterTopBar
      tabs={tabs}
      activeId={active}
      onTabChange={(id) => {
        if (id === 'malaffi') {
          onOpenMalaffi?.();
          return;
        }
        onTabChange(id === 'chart' ? 'chart' : 'encounter');
      }}
      onBack={onBack}
      ariaLabel="Patient workspace"
      // Frame v2 only (this strip is the frame v2 workspace header; signal-ds 2.20, FV2-02): the inactive
      // tabs in text-secondary (AA). The ARIA tabs (roving tabindex) are the 2.20 default.
      labelTone="secondary"
      {...(endSlot != null ? { endSlot: <div className="me-4 flex min-w-0 items-center">{endSlot}</div> } : {})}
    />
  );
}

/** Room/time + RFV (DS VisitInfoStrip) + status pill — top of the LEFT column.
 *  `statusSlot` (preferred) lets the host mount the INTERACTIVE status control here — the
 *  frame places the status dropdown in this block, not on the person card; a bare
 *  `appointmentStatus` string falls back to the read-only StatusChip. */
export function PreEncounterContextBlock({
  room,
  startISO,
  reason,
  appointmentStatus,
  statusSlot,
}: Pick<PreEncounterVisitContext, 'room' | 'startISO' | 'reason' | 'appointmentStatus'> & {
  statusSlot?: ReactNode;
}) {
  const time = formatVisitTime(startISO);
  const cells: VisitInfoCell[] = [];
  // Room/time cell: room is the eyebrow when it resolves; a time with no room still shows
  // (eyebrow "TIME") — never a fabricated room name.
  if (time) cells.push({ id: 'room-time', caption: room?.trim() || 'Time', value: time });
  if (reason?.trim()) cells.push({ id: 'rfv', caption: 'Reason for Visit', value: reason.trim() });

  const status = appointmentStatus?.trim();
  if (cells.length === 0 && !status && !statusSlot) return null;

  return (
    <div
      // PROD-1588 layering fix (Samer 2026-07-22, screenshot: "'Today' is shifted to the very
      // left"): the room/time/RFV/status block was a NAKED row flush on the canvas at x=56 —
      // the E2 frame (1736:156432) seats this row INSIDE the Person card's white band
      // (padX 16 / padY 8). The strip now carries the same card language as the person card
      // below it (surface-card + subtle border + radius + shadow-2), so it reads as
      // foreground and the time value is padded off the column edge.
      className="flex flex-row items-center gap-3 py-2 px-4 bg-surface-card border border-border-subtle rounded-lg shadow-2 font-sans"
    >
      {cells.length > 0 && (
        <div className="flex-1 min-w-0">
          <VisitInfoStrip cells={cells} />
        </div>
      )}
      {statusSlot ??
        (status && (
          /* PM's palette + naming (`@/lib/pm-appointment-status`), the same mapper the encounter
             pill and the Today card use — so one status cannot render two different colours on one
             screen. Was `appointmentStatusSemantic` (the DS canonical map): that painted Cancelled
             RED here while the pill beside it went grey.

             `tone` STAYS "solid" deliberately. This component uses StatusChip directly, so it COULD
             pass "soft" and reach PM's blue tint for Booked/CheckedIn — the two buckets blocked
             elsewhere (see PM_COLOUR_BLOCKED). Doing so here alone would make Booked blue in this
             strip and grey on the status pill two rows away. Uniformly-wrong beats
             inconsistently-right; when the DS `tone` passthrough lands, all three move together. */
          <StatusChip semantic={pmStatusSemantic(status)} tone="solid">
            {pmStatusLabel(status, status)}
          </StatusChip>
        ))}
    </div>
  );
}
